import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  ListTodo, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  Pause,
  Circle
} from 'lucide-react';
import { tasksApi, Task } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { isTaskOverdue } from '../utils/overdue';

const statusConfig = {
  CREATED: { label: 'Создана', icon: Circle, color: 'text-gray-500', badge: 'default' as const },
  IN_PROGRESS: { label: 'В работе', icon: PlayCircle, color: 'text-blue-500', badge: 'info' as const },
  PENDING_APPROVAL: { label: 'Ожидает подтверждения', icon: Clock, color: 'text-yellow-500', badge: 'warning' as const },
  COMPLETED: { label: 'Выполнена', icon: CheckCircle2, color: 'text-green-500', badge: 'success' as const },
  PAUSED: { label: 'Пауза', icon: Pause, color: 'text-yellow-500', badge: 'warning' as const },
  FAILED: { label: 'Не выполнена', icon: AlertTriangle, color: 'text-red-500', badge: 'danger' as const },
  NOT_SELECTED: { label: 'Не выбран', icon: Circle, color: 'text-gray-400', badge: 'default' as const },
};

const priorityConfig = {
  CRITICAL: { label: 'Сверхважная', color: 'bg-red-500' },
  HIGH: { label: 'Важная', color: 'bg-orange-500' },
  CURRENT: { label: 'Текущая', color: 'bg-green-500' },
};

type FilterType = 'all' | 'active' | 'overdue' | 'completed';

export function MyTasksPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const [filter, setFilter] = useState<FilterType>('all');

  const { data: tasks, isLoading, error } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: tasksApi.myTasks,
  });

  const isOverdue = (task: Task): boolean => isTaskOverdue(task);

  const filteredTasks = tasks?.filter((task) => {
    switch (filter) {
      case 'active':
        return ['CREATED', 'IN_PROGRESS'].includes(task.status);
      case 'overdue':
        return isOverdue(task);
      case 'completed':
        return task.status === 'COMPLETED';
      default:
        return true;
    }
  }) || [];

  const stats = {
    total: tasks?.length || 0,
    active: tasks?.filter(t => ['CREATED', 'IN_PROGRESS'].includes(t.status)).length || 0,
    overdue: tasks?.filter(t => isOverdue(t)).length || 0,
    completed: tasks?.filter(t => t.status === 'COMPLETED').length || 0,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500 mb-4">Не удалось загрузить задачи</p>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Заголовок */}
      <div className="p-4 bg-tg-secondary-bg">
        <h1 className="text-2xl font-bold text-tg-text">Мои задачи</h1>
        
        {/* Быстрые фильтры-счётчики */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 -mx-1 px-1">
          <button
            onClick={() => { hapticFeedback('light'); setFilter('all'); }}
            className={`
              flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all
              ${filter === 'all' 
                ? 'bg-tg-button text-white' 
                : 'bg-tg-bg text-tg-text'
              }
            `}
          >
            Все ({stats.total})
          </button>
          <button
            onClick={() => { hapticFeedback('light'); setFilter('active'); }}
            className={`
              flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all
              ${filter === 'active' 
                ? 'bg-blue-500 text-white' 
                : 'bg-tg-bg text-tg-text'
              }
            `}
          >
            <PlayCircle className="w-4 h-4 inline mr-1" />
            В работе ({stats.active})
          </button>
          {stats.overdue > 0 && (
            <button
              onClick={() => { hapticFeedback('light'); setFilter('overdue'); }}
              className={`
                flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all
                ${filter === 'overdue' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-red-100 text-red-600'
                }
              `}
            >
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Просрочено ({stats.overdue})
            </button>
          )}
          <button
            onClick={() => { hapticFeedback('light'); setFilter('completed'); }}
            className={`
              flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all
              ${filter === 'completed' 
                ? 'bg-green-500 text-white' 
                : 'bg-tg-bg text-tg-text'
              }
            `}
          >
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Готово ({stats.completed})
          </button>
        </div>
      </div>

      {/* Список задач */}
      <div className="p-4 space-y-3">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <ListTodo className="w-16 h-16 mx-auto text-tg-hint mb-4" />
            <p className="text-tg-hint">
              {filter === 'all' 
                ? 'У вас пока нет задач'
                : 'Нет задач с выбранным фильтром'
              }
            </p>
          </div>
        ) : (
          filteredTasks.map((task) => {
            const status = statusConfig[task.status as keyof typeof statusConfig] || statusConfig.NOT_SELECTED;
            const priority = priorityConfig[task.priority as keyof typeof priorityConfig];
            const overdue = isOverdue(task);
            const StatusIcon = status.icon;
            
            return (
              <Card
                key={task.id}
                variant="interactive"
                onClick={() => {
                  hapticFeedback('light');
                  navigate(`/tasks/${task.id}`);
                }}
                className={`p-4 ${overdue ? 'border-l-4 border-l-red-500' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {/* Индикатор приоритета */}
                  {priority && (
                    <div className={`w-1 h-full min-h-[60px] rounded-full ${priority.color}`} />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    {/* Код и статус */}
                    <div className="flex items-center gap-2 mb-1">
                      {task.code && (
                        <span className="text-xs font-mono text-tg-hint bg-tg-bg px-2 py-0.5 rounded">
                          {task.code}
                        </span>
                      )}
                      <Badge variant={status.badge} size="sm">
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                    
                    {/* Текст задачи */}
                    <p className="text-tg-text font-medium line-clamp-2">
                      {task.text}
                    </p>
                    
                    {/* Информация о проекте и протоколе */}
                    {task.section?.protocol && (
                      <p className="text-xs text-tg-hint mt-1 truncate">
                        {task.section.protocol.project?.name} → {task.section.protocol.title}
                      </p>
                    )}
                    
                    {/* Дата */}
                    {task.dueDate && (
                      <div className={`flex items-center gap-1 mt-2 text-xs ${overdue ? 'text-red-500' : 'text-tg-hint'}`}>
                        <Clock className="w-3.5 h-3.5" />
                        <span>
                          {overdue ? 'Просрочено: ' : 'Срок: '}
                          {format(new Date(task.dueDate), 'd MMM yyyy', { locale: ru })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

